<template>
  <!-- GlobalToasts.vue — Bar Experts Mitosis & Effervescent Toaster -->
  <div class="global-toasts-container" :class="{ 'is-dark': themeStore.isDark }">

    <!-- Motor SVG de Mitose Líquida (Gooey Filter) -->
    <svg style="visibility: hidden; position: absolute;" width="0" height="0">
      <defs>
        <filter id="toast-mitosis" x="-30%" y="-150%" width="160%" height="500%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="9" result="blur" />
          <feColorMatrix in="blur" mode="matrix"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 28 -13" result="gooey" />
          <feComposite in="SourceGraphic" in2="gooey" operator="atop" />
        </filter>
      </defs>
    </svg>

    <!--
      CAMADA 1 — Blobs Físicos Orgânicos (position: absolute, overlay)
      Fornece a COR e FORMA orgânica líquida via SVG gooey filter.
    -->
    <div class="physics-layer">
      <transition-group name="blob-anim" tag="div" class="blob-stack">
        <div
          v-for="toast in notificationStore.toasts"
          :key="'b-' + toast.id"
          :class="['blob', `blob-${toast.level}`]"
        >
          <!-- Ghost Twin: garante que o blob tenha EXATAMENTE a mesma altura do card real -->
          <div class="ghost-twin" aria-hidden="true">
            <div class="be-toast-content">
              <div class="be-toast-icon">
                <i class="bi bi-circle-fill"></i>
              </div>
              <div class="be-toast-body">
                <strong v-if="toast.level === 'critical'">Erro Crítico do Sistema</strong>
                <strong v-else-if="toast.level === 'error'">Ops! Houve um erro.</strong>
                <strong v-else-if="toast.level === 'warning'">Aviso</strong>
                <strong v-else-if="toast.level === 'info'">Informação</strong>
                <strong v-else>Sucesso!</strong>
                <p>{{ toast.message }}</p>
                <div v-if="toast.code" class="be-toast-code-box">
                  <code>{{ toast.code }}</code>
                </div>
              </div>
              <div class="be-toast-close ms-2">
                <div style="width: 24px; height: 24px;"></div>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <!--
      CAMADA 2 — Conteúdo Semântico (fluxo normal, DEFINE a altura do container)
      background: transparent — o blob abaixo fornece a cor e forma líquida.
    -->
    <transition-group name="card-anim" tag="div" class="card-stack">
      <div
        v-for="toast in notificationStore.toasts"
        :key="toast.id"
        :class="['toast-card', `toast-card-${toast.level}`]"
      >
        <div class="be-toast-content">
          <!-- Ícone Semântico -->
          <div class="be-toast-icon">
            <i v-if="toast.level === 'success'" class="bi bi-check2-circle"></i>
            <i v-else-if="toast.level === 'info'" class="bi bi-info-circle-fill"></i>
            <i v-else-if="toast.level === 'warning'" class="bi bi-exclamation-triangle-fill"></i>
            <i v-else-if="toast.level === 'error'" class="bi bi-x-circle-fill"></i>
            <i v-else-if="toast.level === 'critical'" class="bi bi-shield-x"></i>
          </div>

          <!-- Texto e Título -->
          <div class="be-toast-body">
            <strong v-if="toast.level === 'critical'">Erro Crítico do Sistema</strong>
            <strong v-else-if="toast.level === 'error'">Ops! Houve um erro.</strong>
            <strong v-else-if="toast.level === 'warning'">Aviso</strong>
            <strong v-else-if="toast.level === 'info'">Informação</strong>
            <strong v-else-if="toast.level === 'success'">Sucesso!</strong>
            
            <p>{{ toast.message }}</p>

            <!-- Box de Código para Erros Críticos -->
            <div v-if="toast.level === 'critical' && toast.code" class="be-toast-code-box">
              <code>{{ toast.code }}</code>
              <button class="btn btn-sm text-white border-0 py-0 px-2" @click="copyCode(toast.code)" title="Copiar código">
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
          </div>

          <!-- Botão Fechar -->
          <div class="be-toast-close ms-2">
            <button
              class="btn-close btn-close-white"
              style="opacity: 0.85;"
              @click="notificationStore.removeToast(toast.id)"
              aria-label="Fechar"
            ></button>
          </div>
        </div>
      </div>
    </transition-group>

  </div>
</template>

<script setup>
import { watch } from 'vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { useThemeStore } from '@/stores/theme';

const notificationStore = useNotificationStore();
const themeStore = useThemeStore();

const copyCode = (code) => {
  navigator.clipboard.writeText(code);
  notificationStore.notifySuccess('Código copiado para a área de transferência!');
};

// Dispara uma rajada efervescente de bolhas toda vez que um novo toast entra em cena!
watch(
  () => notificationStore.toasts.length,
  (newLen, oldLen) => {
    if (newLen > oldLen) {
      // Posição de origem das bolhas: topo no mobile, canto inferior no desktop
      const isMobile = window.innerWidth <= 1080;
      const originX = isMobile ? window.innerWidth / 2 : window.innerWidth - 220;
      const originY = isMobile ? 90 : window.innerHeight - 90;

      window.dispatchEvent(
        new CustomEvent('burst-bubbles', {
          detail: { x: originX, y: originY, count: isMobile ? 16 : 26 }
        })
      );
    }
  }
);
</script>

<style scoped>
/* ================================================================
   CONTAINER PRINCIPAL
   ================================================================ */
.global-toasts-container {
  position: fixed;
  z-index: 10550;
  pointer-events: none;
}

/* Desktop: Canto inferior direito acima do footer */
@media (min-width: 1081px) {
  .global-toasts-container {
    bottom: 90px;
    right: 24px;
    width: 440px;
  }
}

/* Mobile: Topo centralizado */
@media (max-width: 1080px) {
  .global-toasts-container {
    top: calc(env(safe-area-inset-top, 0px) + 16px);
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 410px;
  }
}

/* ================================================================
   CAMADA FÍSICA — Blobs com Gooey Filter (Mitose Líquida)
   ================================================================ */
.physics-layer {
  position: absolute;
  inset: 0;
  filter: url('#toast-mitosis');
  overflow: visible;
  pointer-events: none;
  z-index: 1;
}

.blob-stack {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 12px;
}

.blob {
  width: 440px;
  max-width: 100%;
  border-radius: 12px;
  will-change: transform, border-radius;
  transform-origin: bottom center;
}

/* Ghost Twin para manter a mesma altura */
.ghost-twin {
  visibility: hidden;
  pointer-events: none;
}

/* ================================================================
   PALETA OFICIAL DE VERMELHOS ESCARLATE & CARMESIM (SEM RUBI)
   ================================================================ */

/* 1. SUCESSO — Vermelho Escarlate Vibrante */
.blob-success   { background-color: #dc2626; }

/* 2. INFORMAÇÃO — Vermelho Escarlate Profundo */
.blob-info      { background-color: #b91c1c; }

/* 3. ERRO — Vermelho Carmesim Bar Experts */
.blob-error     { background-color: #dc2626; }

/* 4. CRÍTICO — Vermelho Borgonha Intenso */
.blob-critical  { background-color: #991b1b; }

/* 5. AVISO — Âmbar / Dourado (Exceção para Casos Isolados) */
.blob-warning   { background-color: #d97706; }

/* ================================================================
   ANIMAÇÃO UNIFICADA DE MITOSE LÍQUIDA
   ================================================================ */
.blob-anim-enter-active,
.card-anim-enter-active {
  transition:
    transform     0.8s  cubic-bezier(0.34, 1.4, 0.64, 1),
    border-radius 0.75s cubic-bezier(0.25, 1.0, 0.5, 1);
}

.blob-anim-leave-active,
.card-anim-leave-active {
  transition:
    transform     0.5s cubic-bezier(0.55, 0, 1, 0.45),
    border-radius 0.45s ease-in;
}

.blob-anim-enter-from, .card-anim-enter-from,
.blob-anim-leave-to,   .card-anim-leave-to {
  transform: scale(0.1) !important;
  border-radius: 50% !important;
}

.blob-anim-move,
.card-anim-move {
  transition: transform 0.45s ease;
}

/* ================================================================
   CAMADA DE CONTEÚDO — Cards Transparentes
   ================================================================ */
.card-stack {
  position: relative;
  z-index: 2;
  pointer-events: none;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 12px;
}

.toast-card {
  width: 440px;
  max-width: 100%;
  border-radius: 12px;
  pointer-events: auto;
  color: #ffffff;
  background: transparent !important;
  will-change: transform, border-radius;
  transform-origin: bottom center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* ================================================================
   ESTILOS INTERNOS COMPARTILHADOS
   ================================================================ */
.be-toast-content {
  display: flex;
  padding: 15px 18px;
  align-items: flex-start;
  gap: 14px;
}

.be-toast-icon {
  font-size: 1.35rem;
  line-height: 1;
  margin-top: 1px;
  flex-shrink: 0;
}

.be-toast-body {
  flex-grow: 1;
  min-width: 0;
}

.be-toast-body strong {
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.2px;
  margin-bottom: 2px;
}

.be-toast-body p {
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.4;
  opacity: 0.95;
  word-break: break-word;
}

.be-toast-code-box {
  background: rgba(0, 0, 0, 0.35);
  padding: 6px 10px;
  border-radius: 6px;
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.be-toast-code-box code {
  color: #ffffff !important;
  font-weight: bold;
  font-size: 0.78rem;
}

.be-toast-close {
  flex-shrink: 0;
}

/* Mobile */
@media (max-width: 1080px) {
  .blob-stack, .card-stack {
    align-items: center;
    flex-direction: column-reverse;
  }
  .blob, .toast-card {
    width: 100%;
  }
}
</style>
